import { useMemo, useState } from "react";
import Close from "@mui/icons-material/Close";
import ContentCopy from "@mui/icons-material/ContentCopy";
import MoreVert from "@mui/icons-material/MoreVert";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import TableChart from "@mui/icons-material/TableChart";
import {
  Box,
  CircularProgress,
  DialogContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { maxBy } from "lodash-es";

import type {
  AuditTrailEntry,
  ExtendedThalamosUser,
  FormContextData,
  FormIdAndVersion,
} from "@thalamos/aspire-common";
import {
  AuditTrailEntryType,
  getBaseFormTemplate,
  getLatestVersion,
  tryGetAuditData,
  VisionPolicePresenceRequestedActionType,
} from "@thalamos/aspire-common";

import { apiHooks } from "../../../../api.js";
import { entries } from "./Entries.js";
import {
  copyActivityLogToClipboard,
  downloadActivityLogCsv,
  downloadActivityLogPdf,
  extractActivityLogRows,
  formatNhsNumber,
  type ActivityLogTrackingContext,
  type ExportMetadata,
} from "./exportActivityLog.js";
import {
  generateMergeMessage,
  generateUnmergeMessage,
  generateUpdatePatientMessage,
} from "./PatientActivityLog.js";
import { FormTitle, PopupDialog } from "~/components/design-system/index.js";
import { ukLocalFormatDate, ukLocalFormatTime } from "~/util.js";

function getSignedFormTemplate(
  formContextData: FormContextData,
  audit: AuditTrailEntry,
) {
  const signedForm = formContextData.forms.find(
    (f) => f.id === audit.data.formId,
  );
  const signedFormTemplate = signedForm
    ? getBaseFormTemplate(signedForm.formTemplate)
    : null;

  return { signedFormTemplate, signedForm };
}

function getFormTemplate(
  formContextData: FormContextData,
  audit: AuditTrailEntry,
) {
  const form = formContextData.forms.find((f) => f.id === audit.data.formId);
  const formTemplate = form ? getBaseFormTemplate(form.formTemplate) : null;

  return formTemplate;
}

function getAuditUserResourceName(audit: AuditTrailEntry): string | null {
  return (
    audit.resources.find((resource) => resource.resourceType === "user")
      ?.resourceName ?? null
  );
}

function getInterviewInviteRecipientName(
  audit: AuditTrailEntry,
  contactTarget: string | null | undefined,
  recipientTeamName: string | null | undefined,
): string | null {
  if (contactTarget === "team") {
    return recipientTeamName ?? getAuditUserResourceName(audit);
  }

  return getAuditUserResourceName(audit) ?? recipientTeamName ?? null;
}

function formatInterviewTimeRangeLines(
  timeRanges: Array<{ start: string; end: string }> | null | undefined,
): string[] {
  if (!timeRanges || timeRanges.length === 0) return [];

  return timeRanges.map((timeRange) => {
    const date = ukLocalFormatDate(timeRange.start);
    const startTime = ukLocalFormatTime(timeRange.start);
    const endTime = ukLocalFormatTime(timeRange.end);
    return `${date} ${startTime} - ${endTime}`;
  });
}

// The "{user} ({team})" / "{team}" recipient label for a work request. Typed loosely
// so it can render from the raw payload on a parse miss (defence-in-depth: a schema
// drift must never silently blank the recipient name — see the createWorkRequest
// fallback below).
function formatWorkRequestRecipient(source: {
  assignedUserName?: unknown;
  teamName?: unknown;
}): string | undefined {
  const user =
    typeof source.assignedUserName === "string"
      ? source.assignedUserName
      : undefined;
  const team =
    typeof source.teamName === "string" ? source.teamName : undefined;

  if (user !== undefined) {
    return team !== undefined ? `${user} (${team})` : user;
  }
  return team;
}

export function getMessage(
  audit: AuditTrailEntry,
  formContextData?: FormContextData | null,
): string | string[] | null {
  if (!formContextData) return null;
  const formTemplate = getFormTemplate(formContextData, audit);
  // An audit entry can outlive the form's presence in the context payload (the
  // reader may lack access to it, or it may sit in another context). Naming it
  // generically keeps the entry readable; interpolating the absent template
  // spells "Form undefined: Section undefined undefined undefined".
  const formName = formTemplate
    ? `Form ${String(formTemplate.formName)}: Section ${String(
        formTemplate.section,
      )} ${String(formTemplate.description && ` -`)} ${String(formTemplate.description)}`
    : "this form";

  const oneFormPart = formTemplate?.parts?.length === 1;

  const partText = oneFormPart
    ? ""
    : ` part ${String(audit.data.formPart)} of `;

  switch (audit.type) {
    case AuditTrailEntryType.startSoadAssessmentAmendment:
      return `Started a SOAD assessment amendment`;
    case AuditTrailEntryType.cancelSoadAssessmentAmendment:
      return `Cancelled the SOAD assessment amendment`;

    case AuditTrailEntryType.recordSoadDisclosureHarm:
      // Every judgement is audited, but only a change in one is surfaced here:
      // a restriction being recorded, or an earlier one being withdrawn. A null
      // message drops the entry from the log and from its export.
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.recordSoadDisclosureHarm,
      ).match(
        (data) => {
          // Named from the payload's own template rather than `formName`: the
          // shared lookup keys on a `formId` this payload does not carry, and a
          // certificate is known by its short name.
          const certificate =
            getBaseFormTemplate(data.formTemplate)?.formName ?? "this form";

          if (data.disclosureHarmful === true) {
            return `Recorded on ${certificate} that disclosure of the reasons to the patient would be likely to cause serious harm`;
          }

          // A "no" is only worth surfacing where it withdrew a restriction an
          // earlier version carried. On a certificate that never restricted
          // disclosure it is the ordinary answer, and showing it would bury the
          // entries a reader is looking for.
          if (data.previousDisclosureHarmful === true) {
            return `Removed the restricted disclosure previously recorded on ${certificate}`;
          }

          return null;
        },
        // The judgement could not be read, so show the entry rather than risk
        // hiding a restriction.
        () =>
          `Recorded whether disclosure of the reasons to the patient would be likely to cause serious harm`,
      );

    case AuditTrailEntryType.visionPoliceEndIncident:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.visionPoliceEndIncident,
      ).match(
        (data) =>
          `Officer ${String(data.user?.name)} marked the incident as ended.`,
        () => "An officer marked the incident as ended.",
      );

    case AuditTrailEntryType.visionPoliceAcceptRequestToStay:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.visionPoliceAcceptRequestToStay,
      ).match(
        (data) =>
          `Officer ${String(data.user?.name)} confirmed request for ongoing police presence at Place of Safety: ${String(data.placeOfSafety?.name)}. ${data.reason ? `The following condition/reason was provided: ${data.reason}.` : ""}`,
        () => "An officer confirmed request for ongoing police presence.",
      );

    case AuditTrailEntryType.visionPoliceDeclineRequestToStay:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.visionPoliceDeclineRequestToStay,
      ).match(
        (data) =>
          `Officer ${String(data.user?.name)} rejected request for ongoing police presence at Place of Safety: ${String(data.placeOfSafety?.name)}. ${data.reason ? `The following condition/reason was provided: ${data.reason}.` : ""}`,
        () => "An officer rejected request for ongoing police presence.",
      );

    case AuditTrailEntryType.visionAcceptIncident:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.visionAcceptIncident,
      ).match(
        (data) => {
          if (data.incidentType === "mca") {
            return `Mental Capacity Act form accessed by ${String(audit.userName)}. Sent by ${String(data.officerDetails?.name)}.`;
          }
          const posName = data.placeOfSafety?.name ?? "N/A";
          return `Section 136 form accessed at Place of Safety: ${posName}. Sent by officer ${String(data.officerDetails?.name)}`;
        },
        () => `Incident accessed by ${String(audit.userName)}.`,
      );

    case AuditTrailEntryType.visionRejectIncident:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.visionRejectIncident,
      ).match(
        (data) => {
          const formLabel =
            data.incidentType === "mca"
              ? "Mental Capacity Act form"
              : "Section 136 form";
          return `${formLabel} received in error and rejected. Sent by officer ${String(data.officerDetails?.name)}`;
        },
        () => "Incident received in error and rejected.",
      );

    // Retained for historical incidents created before the permanent v2 rollout.
    case AuditTrailEntryType.visionRequestPolicePresence:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.visionRequestPolicePresence,
      ).match(
        (data) => {
          if (
            data.policePresenceRequested?.type ===
            VisionPolicePresenceRequestedActionType.requestPolicePresence
          ) {
            return `Police presence confirmed at Place of Safety: ${String(data.placeOfSafety?.name)}. Ongoing police presence was requested with the following reason provided: ${String(data.policePresenceRequested.reasonLabel)} - ${String(data.policePresenceRequested.furtherReasoning)}.`;
          } else {
            if (
              data.policePresenceRequested
                ?.hasPolicePresenceBeenRequestedLabelValue === "already-left"
            ) {
              return `Police confirmed to have previously been at Place of Safety: ${String(data.placeOfSafety?.name)}.`;
            } else {
              return `Police presence confirmed at Place of Safety: ${String(data.placeOfSafety?.name)} but ongoing police presence was not requested.`;
            }
          }
        },
        () => "Police presence response recorded.",
      );

    case AuditTrailEntryType.visionRequestPolicePresenceV2:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.visionRequestPolicePresenceV2,
      ).match(
        (data) => {
          const messages: string[] = [];

          if (
            data.policePresenceRequested?.hasPolicePresenceRequested?.value ===
            "yes"
          ) {
            messages.push(
              `Ongoing police presence requested at ${data.placeOfSafety?.name ?? "unknown place of safety"} with the following reason provided: ${String(data.policePresenceRequested.policePresenceRequestedReason?.valueLabel)}.`,
            );
          }

          if (
            data.policePresenceRequested?.hasPolicePresenceRequested?.value ===
            "no"
          ) {
            messages.push(
              `Ongoing police presence not needed at ${data.placeOfSafety?.name ?? "unknown place of safety"}.`,
            );
          }

          if (
            data.policePresenceRequested?.hasPolicePresenceRequested?.value ===
            "already-left"
          ) {
            messages.push(
              `Police confirmed to have previously been at ${data.placeOfSafety?.name ?? "unknown place of safety"}.`,
            );
          }

          if (
            data.recordPolicePresence?.hasRemainedAtPlaceOfSafety?.value ===
            "yes"
          ) {
            const officerName = data.recordPolicePresence?.officerName?.value;
            const officerPart = officerName
              ? ` Confirmed by ${officerName}.`
              : "";
            messages.push(
              `Police have remained at place of safety.${officerPart}`,
            );
          }

          if (
            data.recordPolicePresence?.hasRemainedAtPlaceOfSafety?.value ===
            "no"
          ) {
            const officerName = data.recordPolicePresence?.officerName?.value;
            const officerPart = officerName
              ? ` Confirmed by ${officerName}.`
              : "";
            messages.push(
              `Police have not remained at place of safety.${officerPart}`,
            );
          }

          if (
            data.recordPolicePresence?.hasRemainedAtPlaceOfSafety?.value ===
            "yes-stayed-agree-with-nhs"
          ) {
            messages.push(
              `Police confirmed they agree with NHS reasoning and have remained at place of safety.`,
            );
          }

          if (
            data.recordPolicePresence?.hasRemainedAtPlaceOfSafety?.value ===
            "yes-stayed-disagree-with-nhs"
          ) {
            messages.push(
              `Police confirmed they have remained at place of safety but disagree with NHS reasoning.`,
            );
          }

          if (
            data.recordPolicePresence?.hasRemainedAtPlaceOfSafety?.value ===
            "no-escalated-and-left"
          ) {
            messages.push(
              `Police confirmed they escalated and left place of safety.`,
            );
          }

          if (messages.length === 0) return null;

          return messages.join(" ");
        },
        () => null,
      );

    case AuditTrailEntryType.visionResponsePolicePresence:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.visionResponsePolicePresence,
      ).match(
        (data) => {
          const policePresenceRequestData = data.policePresenceRequestData;
          const place = data.placeOfSafety?.name;
          const hpName = data.healthProfessionalName;
          const record = data.recordPolicePresence;

          const messages: string[] = [];

          if (policePresenceRequestData?.hasPolicePresenceRequested === "yes") {
            messages.push(
              `Ongoing police presence requested at ${String(place)} by ${String(hpName)} with the following reason provided: ${String(policePresenceRequestData.policePresenceRequestedReasonLabel)}.`,
            );
          }

          if (policePresenceRequestData?.hasPolicePresenceRequested === "no") {
            messages.push(
              `Ongoing police presence not needed at ${String(place)}.`,
            );
          }

          if (record?.hasRemainedAtPlaceOfSafetyValue === "yes") {
            messages.push(
              `Police confirmed they have remained at ${String(place)}.`,
            );
          }

          if (record?.hasRemainedAtPlaceOfSafetyValue === "no") {
            messages.push(
              `Police confirmed they have not remained at ${String(place)}.`,
            );
          }

          if (
            record?.hasRemainedAtPlaceOfSafetyValue ===
            "yes-stayed-disagree-with-nhs"
          ) {
            messages.push(
              `Police confirmed they have remained at ${String(place)} but disagree with NHS reasoning.`,
            );
          }
          if (
            record?.hasRemainedAtPlaceOfSafetyValue ===
            "yes-stayed-agree-with-nhs"
          ) {
            messages.push(
              `Police confirmed they agree with NHS reasoning and have remained at ${String(place)}.`,
            );
          }

          if (
            record?.hasRemainedAtPlaceOfSafetyValue === "no-escalated-and-left"
          ) {
            messages.push(
              `Police confirmed they escalated and left ${String(place)}.`,
            );
          }

          return messages.length > 0 ? messages.join(" ") : null;
        },
        () => null,
      );

    case AuditTrailEntryType.adminDeleteWorkItem: {
      const teamResource = audit.resources.find(
        (r) => r.resourceType === "team",
      );
      return `A Thalamos Admin deleted this from the caseload of ${String(teamResource?.resourceName)}. This will have been done at the request of the organisation.`;
    }

    case AuditTrailEntryType.mergePatient:
      return generateMergeMessage(audit);

    case AuditTrailEntryType.unmergePatient:
      return generateUnmergeMessage(audit);

    case AuditTrailEntryType.updatePatient:
      return generateUpdatePatientMessage(audit);

    case AuditTrailEntryType.finaliseWorkRequest:
      return "Marked administrative scrutiny as complete";

    case AuditTrailEntryType.unfinaliseWorkRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.unfinaliseWorkRequest,
      ).match(
        (data) => [
          "Legal Scrutiny Undone",
          `Reason: ${String(data.reason)}`,
          `Description: ${String(data.reasonDescription)}`,
        ],
        () => ["Legal Scrutiny Undone"],
      );

    case AuditTrailEntryType.createAmendRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.createAmendRequest,
      ).match(
        (data) => [
          `Requested that ${String(data.assignedUserName)} - ${String(data.teamName)} amend ${partText}${formName} under Section 15 of the Act.`,
          `Reason(s): ${String(data.requestReasons?.map((r) => r.label))}`,
          `Description: ${String(data.requestAdditionalInfo)}`,
        ],
        () => [
          `Requested an amend ${partText}${formName} under Section 15 of the Act.`,
        ],
      );

    case AuditTrailEntryType.createFileNoteRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.createFileNoteRequest,
      ).match(
        (data) => [`File note added: ${String(data.fileNote)}`],
        () => ["File note added"],
      );

    case AuditTrailEntryType.deleteFileNoteRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.deleteFileNoteRequest,
      ).match(
        (data) => [
          `File note(s) deleted: ${String(data.deleteFileNote?.map((n) => n))}`,
        ],
        () => ["File note(s) deleted"],
      );

    case AuditTrailEntryType.unclaimWorkRequest:
      return `${String(audit.userName)} unclaimed this form`;

    case AuditTrailEntryType.claimWorkRequest:
      return `${String(audit.userName)} claimed this form`;

    case AuditTrailEntryType.claimOutcomeForm:
      return tryGetAuditData(audit, AuditTrailEntryType.claimOutcomeForm).match(
        (data) =>
          `${String(audit.userName)} claimed an outcome form for ${data.posDisplayName}`,
        () =>
          `${String(audit.userName)} claimed an outcome form for a place of safety`,
      );

    case AuditTrailEntryType.recordReasonForNotProceedingMedRec:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.recordReasonForNotProceedingMedRec,
      ).match(
        (data) => [
          `Action confirmed: no medical recommendation made`,
          `Reason: ${String(data.reason)}`,
          ...(data.reasonDescription
            ? [`Description: ${data.reasonDescription}`]
            : []),
        ],
        () => [`Action confirmed: no medical recommendation made`],
      );

    case AuditTrailEntryType.recordReasonForNotProceedingNoApp:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.recordReasonForNotProceedingNoApp,
      ).match(
        (data) => [
          `Action confirmed: no application made`,
          `Reason: ${String(data.reason)}`,
          ...(data.reasonDescription
            ? [`Description: ${data.reasonDescription}`]
            : []),
        ],
        () => [`Action confirmed: no application made`],
      );

    case AuditTrailEntryType.reopenAdmission:
      return tryGetAuditData(audit, AuditTrailEntryType.reopenAdmission).match(
        (data) => [
          `Action confirmed: Admission is reopened`,
          `Reason: ${String(data.reason)}`,
          ...(data.reasonDescription
            ? [`Description: ${data.reasonDescription}`]
            : []),
        ],
        () => [`Action confirmed: Admission is reopened`],
      );

    case AuditTrailEntryType.autoCloseAdmissionNoMedRec:
      return [
        `Admission automatically closed after 28 days — outcome: Non-detention decision`,
      ];

    case AuditTrailEntryType.declineWorkRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.declineWorkRequest,
      ).match(
        (data) => {
          const isAmend = data.requestType === "amend";
          const formId = audit.resources.find(
            (f) => f.resourceType === "form",
          )?.resourceId;
          const form = formContextData.forms.find((f) => f.id === formId);
          const template = form && getBaseFormTemplate(form);
          const formDescription = template
            ? `Form ${template?.formName}: Section ${template?.section}` +
              `${template?.description && ` -`} ${template?.description}`
            : "a form";
          return [
            isAmend
              ? `Declined to amend ${formDescription} under Section 15 of the Act. `
              : "Declined to work on this assessment",
            `Reason: ${String(data.reason)}`,
            ...(data.reasonDescription
              ? [`Description: ${data.reasonDescription}`]
              : []),
          ];
        },
        () => ["Declined to work on this assessment"],
      );

    case AuditTrailEntryType.acceptWorkRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.acceptWorkRequest,
      ).match(
        (data) =>
          data.requestType === "amend"
            ? `Accepted request to amend ${partText}${formName} under Section 15 of the Act. `
            : `Accepted the request to work on this ${
                formContextData.type === "admission" ? "MHA assessment" : "form"
              }`,
        () =>
          `Accepted the request to work on this ${
            formContextData.type === "admission" ? "MHA assessment" : "form"
          }`,
      );

    case AuditTrailEntryType.cancelAmendRequest: {
      return `Cancelled the section 15 amend.`;
    }

    case AuditTrailEntryType.expireAmendRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.expireAmendRequest,
      ).match(
        (data) => {
          const formattedExpiryPeriod =
            `${String(data.expiryPeriod.value)}-${data.expiryPeriod.unit}`
              .replace("minutes", "minute")
              .replace("hours", "hour")
              .replace("days", "day");
          return `The ${formattedExpiryPeriod} rectification period has expired. Form can no longer be amended.`;
        },
        () =>
          "The rectification period has expired. Form can no longer be amended.",
      );

    case AuditTrailEntryType.expireForm:
      return `Section 4 has expired. Part 2 of Form H3 was not completed within the 72 hour period.`;

    case AuditTrailEntryType.createFormContext:
      return `Started this ${
        formContextData.type === "admission"
          ? "MHA assessment"
          : formContextData.type === "soad-assessment"
            ? "SOAD assessment"
            : "form"
      }`;

    case AuditTrailEntryType.deleteForm:
      // This is a redundant thing to show for a standalone form
      if (formContextData.type === "standalone") {
        return null;
      }
      return tryGetAuditData(audit, AuditTrailEntryType.deleteForm).match(
        (data) => {
          const template = getBaseFormTemplate(
            data.formTemplate as FormIdAndVersion,
          );

          return `Deleted a Form ${String(template?.formName)} ${String(template?.section && ": Section")} ${String(
            template?.section,
          )} ${String(template?.description && `-`)} ${String(template?.description)}`;
        },
        () => null,
      );

    case AuditTrailEntryType.createForm:
      // This is a redundant thing to show for a standalone form
      if (formContextData.type === "standalone") {
        return null;
      }
      return tryGetAuditData(audit, AuditTrailEntryType.createForm).match(
        (data) => {
          const template = getBaseFormTemplate(
            data.formTemplate as FormIdAndVersion,
          );

          return `Started a Form ${String(template?.formName)}${String(template?.section && ": Section")} ${String(
            template?.section,
          )} ${String(template?.description && `-`)} ${String(template?.description)}`;
        },
        () => null,
      );

    case AuditTrailEntryType.uploadForm:
      // This is a redundant thing to show for a standalone form
      if (formContextData.type === "standalone") {
        return null;
      } else {
        const formId = audit.resources.find(
          (f) => f.resourceType === "form",
        )?.resourceId;
        const form = formContextData.forms.find((f) => f.id === formId);
        const template = form && getBaseFormTemplate(form);

        return `Uploaded a Form ${String(template?.formName)}: Section ${String(
          template?.section,
        )}${String(template?.description && `-`)} ${String(template?.description)}`;
      }

    case AuditTrailEntryType.deleteUploadedForm: {
      const formId = audit.resources.find(
        (f) => f.resourceType === "form",
      )?.resourceId;
      const form = formContextData.forms.find((f) => f.id === formId);
      const template = form && getBaseFormTemplate(form);

      return `Deleted an uploaded Form ${String(template?.formName)}: Section ${String(
        template?.section,
      )}${String(template?.description && `-`)} ${String(template?.description)}`;
    }

    case AuditTrailEntryType.signForm:
      // if audit is greater than one then its an amend
      return tryGetAuditData(audit, AuditTrailEntryType.signForm).match(
        (data) => {
          if (formContextData.type === "soad-assessment") {
            const template = getBaseFormTemplate(
              data.formTemplate as FormIdAndVersion,
            );
            return `Signed ${String(template?.formName)}`;
          }

          const formPart = data.part;
          if (data.formVersion > 1) {
            return `Amended ${
              oneFormPart ? "" : ` part ${String(formPart)} of `
            }${formName} under Section 15 of the Act.`;
          }

          if (formTemplate?.parts.length === 1) {
            return `Signed ${formName}`;
          }

          return `Signed part ${String(formPart)} of a ${formName}`;
        },
        () => `Signed ${formName}`,
      );

    case AuditTrailEntryType.createFormPushEvent: {
      const formId = audit.resources.find(
        (f) => f.resourceType === "form",
      )?.resourceId;
      const form = formContextData.forms.find((f) => f.id === formId);
      const template = form && getBaseFormTemplate(form);

      const externalSystemType =
        audit.resources.find((f) => f.resourceType === "rioInstance") !==
        undefined
          ? "Rio"
          : "External System";

      const resolvedFormName =
        template?.formName ??
        (form?.formTemplate.id === "mha-136" ? "S136" : undefined);

      const formName = [
        resolvedFormName,
        template?.section && `: Section ${template?.section}`,
        template?.description && ` - ${template?.description}`,
      ]
        .filter((x) => x !== undefined)
        .join(" ");

      return `Form ${formName} uploaded to ${externalSystemType}`;
    }

    case AuditTrailEntryType.createWorkRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.createWorkRequest,
      ).match(
        (data) => {
          switch (data.requestType) {
            case "medical-recommendation":
              return `Requested a medical recommendation from ${String(data.teamName)}`;
            case "admission":
              return `Requested an admission from ${String(data.teamName)}`;
            case "record-of-detention":
              return `Requested a record of detention form (H3) from ${String(data.teamName)}`;
            case "finalise":
              return `Requested administrative scrutiny from ${String(data.teamName)}`;
            case "complete": {
              const requestedForm = formContextData.forms.find(
                (f) => f.id === data.formId,
              );
              const requestedFormTemplate = requestedForm
                ? getBaseFormTemplate(requestedForm.formTemplate)
                : null;

              const requestedParty = data.assignedUserName
                ? `${data.assignedUserName} (${String(data.teamName)})`
                : data.teamName;

              const { signedFormTemplate, signedForm } = getSignedFormTemplate(
                formContextData,
                audit,
              );

              const latestVersion = getLatestVersion(signedForm);

              const signatureWithMaxPart = maxBy(
                latestVersion?.signatures,
                "part",
              );

              let partText;

              if (signedFormTemplate?.parts.length === 1) {
                partText = "";
              }

              const signedFormTemplateLength =
                signedFormTemplate?.parts?.length ?? 0;

              const signatureWithMaxParts = signatureWithMaxPart?.part ?? 0;

              if (signedFormTemplateLength > 1 && signatureWithMaxParts > 0) {
                partText = ` part ${String(data.formPart)} of`;
              }

              const descriptionText = requestedFormTemplate?.description
                ? ` - ${requestedFormTemplate.description}`
                : "";

              return `Requested that ${String(requestedParty)} sign${partText ?? ""} Form ${String(
                requestedFormTemplate?.formName,
              )}: Section ${String(requestedFormTemplate?.section)}${descriptionText}`;
            }
          }

          if (
            data.assignedUserId === audit.userId &&
            (data.teamId == null || data.teamId === audit.teamId)
          ) {
            return null;
          }

          const recipient = formatWorkRequestRecipient(data);
          const target =
            formContextData.type === "admission" ? "MHA assessment" : "form";

          return recipient
            ? `Sent a request to ${recipient} to work on this ${target}`
            : `Sent a request to work on this ${target}`;
        },
        (imprecise) => {
          // Parse miss (e.g. a payload shape we don't yet model): still recover the
          // recipient from the raw payload rather than dropping it.
          const recipient = formatWorkRequestRecipient(imprecise.raw);
          const target =
            formContextData.type === "admission" ? "MHA assessment" : "form";

          return recipient
            ? `Sent a request to ${recipient} to work on this ${target}`
            : `Sent a request to work on this ${target}`;
        },
      );

    case AuditTrailEntryType.updateFormContextOfficialReferenceId:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.updateFormContextOfficialReferenceId,
      ).match(
        (data) => {
          const values = [data.from, data.to];
          if (values.some((v) => (v ?? undefined) === undefined)) {
            return "Updated Request Id";
          }
          return `Updated Request Id from "${String(values[0])}" to "${String(values[1])}"`;
        },
        () => "Updated Request Id",
      );

    case AuditTrailEntryType.createDeMinimisRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.createDeMinimisRequest,
      ).match(
        (data) => [
          `Requested a de minimis correction from ${String(data.assignedUserName)} (${String(data.teamName)})`,
          `Error type: ${String(data.errorTypeLabel)}`,
          ...(data.note ? [`Description: ${data.note}`] : []),
        ],
        () => ["Requested a de minimis correction"],
      );

    case AuditTrailEntryType.acceptDeMinimisRequest:
      return `Accepted a de minimis correction request`;

    case AuditTrailEntryType.completeDeMinimisRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.completeDeMinimisRequest,
      ).match(
        (data) => [
          `Completed a de minimis correction`,
          ...(data.signatoryName ? [`Signed by: ${data.signatoryName}`] : []),
          ...(data.response ? [`Response: ${data.response}`] : []),
        ],
        () => ["Completed a de minimis correction"],
      );

    case AuditTrailEntryType.rejectDeMinimisRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.rejectDeMinimisRequest,
      ).match(
        (data) => [
          `Rejected a de minimis correction request`,
          ...(data.rejectedReason ? [`Reason: ${data.rejectedReason}`] : []),
        ],
        () => ["Rejected a de minimis correction request"],
      );

    case AuditTrailEntryType.cancelDeMinimisRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.cancelDeMinimisRequest,
      ).match(
        (data) => [
          `Cancelled a de minimis correction request`,
          ...(data.reason ? [`Reason: ${data.reason}`] : []),
        ],
        () => ["Cancelled a de minimis correction request"],
      );

    // Redo admission forms (non-s15). A redo is whole-form (not per-part), so we
    // describe the form rather than a part.
    case AuditTrailEntryType.createRedoRequest:
    case AuditTrailEntryType.initiateRedo:
      return tryGetAuditData(audit, audit.type).match(
        (data) => {
          const reasonLines = [
            ...(data.requestReasons?.length
              ? [
                  `Reason(s): ${data.requestReasons
                    .map((r) => r.label)
                    .join(", ")}`,
                ]
              : []),
            ...(data.requestAdditionalInfo
              ? [`Description: ${data.requestAdditionalInfo}`]
              : []),
          ];

          if (audit.type === AuditTrailEntryType.initiateRedo) {
            return [`Started a redo of ${formName}.`, ...reasonLines];
          }

          const requestedParty = data.assignedUserName
            ? data.teamName
              ? `${data.assignedUserName} (${data.teamName})`
              : data.assignedUserName
            : (data.teamName ?? "the person who completed the form");
          return [
            `Requested that ${requestedParty} redo ${formName}.`,
            ...reasonLines,
          ];
        },
        () =>
          audit.type === AuditTrailEntryType.initiateRedo
            ? [`Started a redo of ${formName}.`]
            : [`Requested a redo of ${formName}.`],
      );

    case AuditTrailEntryType.retractRedoRequest:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.retractRedoRequest,
      ).match(
        (data) => [
          `Retracted the redo request for ${formName}.`,
          ...(data.reason ? [`Reason: ${data.reason}`] : []),
        ],
        () => [`Retracted the redo request for ${formName}.`],
      );

    case AuditTrailEntryType.acceptRedoRequest:
      return `Accepted the redo request for ${formName}. A new form was created to complete the redo.`;

    case AuditTrailEntryType.declineRedoRequest:
    case AuditTrailEntryType.cancelRedoInProgress:
      return tryGetAuditData(audit, audit.type).match(
        (data) => {
          const action =
            audit.type === AuditTrailEntryType.declineRedoRequest
              ? `Declined the redo request for ${formName}.`
              : `Cancelled the redo of ${formName}.`;
          return [action, ...(data.reason ? [`Reason: ${data.reason}`] : [])];
        },
        () =>
          audit.type === AuditTrailEntryType.declineRedoRequest
            ? [`Declined the redo request for ${formName}.`]
            : [`Cancelled the redo of ${formName}.`],
      );

    case AuditTrailEntryType.completeRedo:
      return `Completed the redo of ${formName}. The original form was voided and replaced with the new signed form.`;

    case AuditTrailEntryType.interviewParticipantAdded:
    case AuditTrailEntryType.interviewParticipantReContacted:
      return tryGetAuditData(audit, audit.type).match(
        (data) => {
          const inviteSource = data.inviteSource ?? "known";
          const recipientName = getInterviewInviteRecipientName(
            audit,
            data.contactTarget,
            data.recipientTeamName,
          );

          if (inviteSource === "known") {
            if (!recipientName) return null;

            return data.contactTarget === "team"
              ? `Requested ${recipientName} to complete an independent assessment.`
              : `Requested ${recipientName}${data.recipientTeamName ? ` (${data.recipientTeamName})` : ""} to complete an independent assessment.`;
          }

          // search flow — may be a grouped synthetic entry
          const groupedNames = data._groupedDoctorNames;
          if (groupedNames && groupedNames.length > 1) {
            return `Contacted the following doctors for an independent assessment: ${groupedNames.join(", ")}.`;
          }

          if (!recipientName) return null;

          return `${
            audit.type === AuditTrailEntryType.interviewParticipantReContacted
              ? "Re-contacted"
              : "Contacted"
          } ${recipientName} for an independent assessment.`;
        },
        () => null,
      );

    case AuditTrailEntryType.interviewParticipantAccepted: {
      const doctorName = getAuditUserResourceName(audit);
      return doctorName
        ? `${doctorName} accepted the request for an independent assessment.`
        : null;
    }

    case AuditTrailEntryType.interviewParticipantRejected: {
      const doctorName = getAuditUserResourceName(audit);
      return doctorName
        ? `${doctorName} declined the request for an independent assessment.`
        : null;
    }

    case AuditTrailEntryType.interviewParticipantConfirmed:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.interviewParticipantConfirmed,
      ).match(
        (data) => {
          const doctorName =
            data.confirmedDoctor?.name ?? getAuditUserResourceName(audit);
          if (!doctorName) return null;

          const timeRangeLines = formatInterviewTimeRangeLines(data.timeRanges);
          const messages = [
            timeRangeLines.length > 0
              ? `Confirmed ${doctorName} for an independent assessment on ${timeRangeLines[0]}.`
              : `Confirmed ${doctorName} for an independent assessment.`,
            ...timeRangeLines.slice(1).map((line) => `${line}.`),
          ];

          const revokedDoctors = (data.revokedDoctors ?? [])
            .map((doctor) => doctor.name)
            .filter((name): name is string => !!name);

          if (revokedDoctors.length > 0) {
            messages.push(
              `The following doctors had their access revoked: ${revokedDoctors.join(", ")}.`,
            );
          }

          return messages;
        },
        () => {
          const doctorName = getAuditUserResourceName(audit);
          return doctorName
            ? `Confirmed ${doctorName} for an independent assessment.`
            : null;
        },
      );

    case AuditTrailEntryType.interviewParticipantRevoked:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.interviewParticipantRevoked,
      ).match(
        (data) => {
          if (data.context !== "unconfirmed") return null;

          const doctorName =
            data.unconfirmedDoctor?.name ?? getAuditUserResourceName(audit);
          if (!doctorName) return null;

          const messages = [
            `Unconfirmed ${doctorName} for an independent assessment.`,
          ];

          const restoredDoctors = (data.restoredDoctors ?? [])
            .map((doctor) => doctor.name)
            .filter((name): name is string => !!name);

          if (restoredDoctors.length > 0) {
            messages.push(
              `The following doctors had their access restored: ${restoredDoctors.join(", ")}.`,
            );
          }

          return messages;
        },
        () => null,
      );

    case AuditTrailEntryType.interviewCancelled:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.interviewCancelled,
      ).match(
        (data) => {
          const doctors = (data.doctors ?? [])
            .map((doctor) => doctor.name)
            .filter((name): name is string => !!name);

          if (doctors.length === 0) return null;

          return `Cancelled the independent assessment with ${doctors.join(", ")}.`;
        },
        () => null,
      );

    case AuditTrailEntryType.interviewUpdated:
      return tryGetAuditData(audit, AuditTrailEntryType.interviewUpdated).match(
        (data) => {
          const doctors = (data.doctors ?? [])
            .map((doctor) => doctor.name)
            .filter((name): name is string => !!name);

          if (doctors.length === 0) return null;

          const changedFields = data.changedFields;
          if (changedFields && changedFields.length > 0) {
            const fieldsSummary = changedFields.join(" and ");
            return `Edited the ${fieldsSummary} for an independent assessment with ${doctors.join(", ")}.`;
          }

          return `Edited the details for an independent assessment with ${doctors.join(", ")}.`;
        },
        () => null,
      );

    // Section 12 payments are a migrated domain, so these read the typed data
    // via `tryGetAuditData`; a legacy/unverified row falls to the imprecise branch.
    case AuditTrailEntryType.section12PaymentRequestCreated:
      return tryGetAuditData(
        audit,
        AuditTrailEntryType.section12PaymentRequestCreated,
      ).match(
        (data) =>
          data.assessmentDateTime
            ? `Requested payment for an independent assessment done on ${ukLocalFormatDate(data.assessmentDateTime)} ${ukLocalFormatTime(data.assessmentDateTime)} from ${data.amhpTeamName ?? "the AMHP team"}.`
            : `Requested payment for an independent assessment from ${data.amhpTeamName ?? "the AMHP team"}.`,
        () =>
          `Requested payment for an independent assessment from the AMHP team.`,
      );

    case AuditTrailEntryType.section12PaymentRequestStatusChanged: {
      const doctorName = getAuditUserResourceName(audit);
      if (!doctorName) return null;

      return tryGetAuditData(
        audit,
        AuditTrailEntryType.section12PaymentRequestStatusChanged,
      ).match(
        (data) => {
          if (data.after.status === "accepted") {
            return `Approved payment request from ${doctorName} for an independent assessment.`;
          }
          if (data.after.status === "rejected") {
            return `Rejected payment request from ${doctorName} for an independent assessment.`;
          }
          return null;
        },
        () => null,
      );
    }

    default:
      return null;
  }
}

export function FormContextActivityLog({
  onClose,
  formContext,
  user,
}: {
  formContext: FormContextData;
  user: ExtendedThalamosUser;
  onClose: () => void;
}) {
  const auditQueryParams = {
    limit: 1000,
    offset: 0,
    fromDate: "2000-01-01",
    toDate: "2050-01-01",
  };

  const [
    { data: auditData, loading: auditDataLoading, response: auditDataResponse },
  ] = apiHooks.audit.searchByFormContextId(formContext.id, auditQueryParams);

  const [
    {
      data: patientAuditData,
      loading: patientAuditDataLoading,
      response: patientAuditDataResponse,
    },
  ] = apiHooks.audit.searchByPatientId(formContext.patientId, auditQueryParams);

  const standaloneForm =
    formContext.type === "standalone" ? formContext.forms[0] : undefined;
  const baseFormTemplate =
    standaloneForm === undefined ? null : getBaseFormTemplate(standaloneForm);

  const loaded =
    !auditDataLoading &&
    !patientAuditDataLoading &&
    auditDataResponse?.status === 200 &&
    patientAuditDataResponse?.status === 200;

  const titleSuffix =
    formContext.type === "police-incident"
      ? formContext.forms.find((f) => f.formTemplate.id === "mha-136")
        ? "136 Police Form"
        : "Mental Capacity Act Police Form"
      : formContext.type === "soad-assessment"
        ? "SOAD Assessment"
        : formContext.type === "admission"
          ? "MHA Assessment"
          : `Form ${String(baseFormTemplate?.formName)}: Section ${String(
              baseFormTemplate?.section,
            )}${String(baseFormTemplate?.description && `-`)} ${String(
              baseFormTemplate?.description,
            )}`;

  const titleText = `Activity Log for ${titleSuffix}`;

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const allEntries = useMemo(
    () =>
      loaded
        ? [...(auditData?.entries ?? []), ...(patientAuditData?.entries ?? [])]
        : [],
    [loaded, auditData, patientAuditData],
  );

  const exportRows = useMemo(
    () =>
      loaded ? extractActivityLogRows(allEntries, getMessage, formContext) : [],
    [loaded, allEntries, formContext],
  );

  const tracking: ActivityLogTrackingContext = useMemo(
    () => ({
      type: "formContext",
      formContextId: formContext.id,
      patientId: formContext.patientId,
    }),
    [formContext.id, formContext.patientId],
  );

  const metadata: ExportMetadata = useMemo(
    () => ({
      patientName: `${formContext.patient.name.family.toUpperCase()}, ${formContext.patient.name.given}`,
      nhsNumber: formatNhsNumber(
        (formContext.patient as { nhsNumber?: string | null }).nhsNumber,
      ),
      formContextId: formContext.id,
      formContextType: titleSuffix,
    }),
    [formContext.patient, formContext.id, titleSuffix],
  );

  const handleExportCopy = () => {
    setMenuAnchorEl(null);
    void copyActivityLogToClipboard(exportRows, tracking, metadata);
  };

  const handleExportCsv = () => {
    setMenuAnchorEl(null);
    downloadActivityLogCsv(exportRows, titleText, tracking, metadata);
  };

  const handleExportPdf = () => {
    setMenuAnchorEl(null);
    downloadActivityLogPdf(exportRows, titleText, tracking, metadata);
  };

  return (
    <PopupDialog open={true} onClose={onClose} fullWidth>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 4,
        }}
      >
        <FormTitle
          titleText={titleText}
          hasContainerMarginBottom={false}
          hasTitleBottomMargin={false}
          useReducedTopPadding={true}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <IconButton
            aria-label="Export activity log"
            onClick={(e) => {
              setMenuAnchorEl(e.currentTarget);
            }}
            disabled={!loaded}
            data-testid="export-activity-log-button"
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={() => {
              setMenuAnchorEl(null);
            }}
          >
            <MenuItem onClick={handleExportCopy}>
              <ListItemIcon>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy to clipboard</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportCsv}>
              <ListItemIcon>
                <TableChart fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as CSV</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportPdf}>
              <ListItemIcon>
                <PictureAsPdf fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as PDF</ListItemText>
            </MenuItem>
          </Menu>
          <IconButton
            sx={{ width: "40px", height: "40px" }}
            data-testid="close-dialog"
            aria-label="Close"
            onClick={onClose}
          >
            <Close sx={{ color: "primary.hint" }} fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <DialogContent>
        {!loaded ? (
          <CircularProgress />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {entries(user, allEntries, getMessage, formContext)}
          </Box>
        )}
      </DialogContent>
    </PopupDialog>
  );
}
